/**
 * `/organizations` — the curated registry.
 *
 * The sentence the screen rests on, and the one people most often get wrong:
 *
 * > Affiliation tells the application where you serve. It grants nothing.
 *
 * Everything on this page is arranged around that. Affiliating is one quiet button, not a
 * membership flow; there is no "request access" anywhere; and the aside says plainly what
 * affiliation does and does not do — because a priest who affiliates with a diocese and then cannot
 * see its liturgies should learn why here, not from a support conversation.
 *
 * The request form checks the name **before** submitting, so somebody is not left waiting days for
 * a review of a name that already exists under a different spelling (ADR-008).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import type { ApiError } from '../../api/client';
import { Notice } from '../../components/primitives';
import {
  affiliate,
  checkName,
  fetchAffiliations,
  fetchMyRequests,
  fetchOrganizations,
  ORGANIZATION_KIND_LABELS,
  requestOrganization,
  unaffiliate,
  withdrawRequest,
  type Organization,
  type OrganizationKind,
} from './api';
import './organizations.css';

export function OrganizationsScreen(): React.JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  const [query, setQuery] = useState('');
  const [kind, setKind] = useState('');
  const [requesting, setRequesting] = useState(false);

  const organizations = useQuery({
    queryKey: ['organizations', query, kind],
    queryFn: () => fetchOrganizations({ q: query, kind }),
  });

  const affiliations = useQuery({ queryKey: ['affiliations'], queryFn: fetchAffiliations });
  const myRequests = useQuery({ queryKey: ['my-organization-requests'], queryFn: fetchMyRequests });

  const affiliated = new Set((affiliations.data?.data ?? []).map((o) => o.id));

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['affiliations'] });
  };

  const join = useMutation({ mutationFn: affiliate, onSuccess: refresh });
  const leave = useMutation({ mutationFn: unaffiliate, onSuccess: refresh });

  const withdraw = useMutation({
    mutationFn: withdrawRequest,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['my-organization-requests'] });
    },
  });

  const error = [organizations.error, join.error, leave.error].find((e) => e instanceof Error) as
    ApiError | undefined;

  return (
    <div className="org">
      <header className="org__head">
        <div>
          <h1 className="org__title">{t('organizations.title')}</h1>
          <p className="org__lede">{t('organizations.lede')}</p>
        </div>
        <Link className="org__link" to="/templates">
          {t('organizations.templates_link')}
        </Link>
      </header>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="org__grid">
        <div className="org__main">
          <section className="org__card">
            <div className="org__filters">
              <input
                className="org__search"
                type="search"
                placeholder={t('organizations.search_placeholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <select value={kind} onChange={(e) => setKind(e.target.value)}>
                <option value="">{t('organizations.every_kind')}</option>
                {(organizations.data?.kinds ?? []).map((k) => (
                  <option key={k} value={k}>
                    {ORGANIZATION_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>

            {organizations.isPending ? (
              <p className="org__fine">{t('organizations.loading')}</p>
            ) : organizations.data?.data.length === 0 ? (
              <div className="org__empty">
                <p className="org__body">{t('organizations.nothing_matches')}</p>
                <button type="button" className="org__ghost" onClick={() => setRequesting(true)}>
                  {t('organizations.request_add')}
                </button>
              </div>
            ) : (
              <ul className="org__list">
                {(organizations.data?.data ?? []).map((organization) => (
                  <Row
                    key={organization.id}
                    organization={organization}
                    affiliated={affiliated.has(organization.id)}
                    busy={join.isPending || leave.isPending}
                    onJoin={() => join.mutate(organization.id)}
                    onLeave={() => leave.mutate(organization.id)}
                  />
                ))}
              </ul>
            )}
          </section>

          {requesting ? (
            <RequestForm
              onDone={async () => {
                setRequesting(false);
                await queryClient.invalidateQueries({ queryKey: ['my-organization-requests'] });
              }}
              onCancel={() => setRequesting(false)}
            />
          ) : (
            <button type="button" className="org__ghost" onClick={() => setRequesting(true)}>
              {t('organizations.request_missing')}
            </button>
          )}

          {(myRequests.data?.data ?? []).length > 0 ? (
            <section className="org__card">
              <p className="org__eyebrow">{t('organizations.your_requests')}</p>
              <ul className="org__list">
                {(myRequests.data?.data ?? []).map((row) => (
                  <li key={row.id} className="org__request">
                    <div>
                      <div className="org__row-name">{row.requested_name}</div>
                      <div className="org__row-meta">
                        {row.created_at.slice(0, 10)}
                        {row.decision_note ? ` · ${row.decision_note}` : ''}
                      </div>
                    </div>
                    <span className={`org-pill org-pill--${row.status}`}>{row.status}</span>
                    {row.status === 'pending' ? (
                      <button
                        type="button"
                        className="org__ghost"
                        onClick={() => withdraw.mutate(row.id)}
                      >
                        {t('organizations.withdraw')}
                      </button>
                    ) : (
                      <span />
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>

        <aside className="org__side">
          <section className="org__card org__card--warm">
            <p className="org__eyebrow">{t('organizations.what_affiliation_title')}</p>
            <p className="org__body">{t('organizations.what_affiliation_desc')}</p>
            <p className="org__body org__body--strong">{t('organizations.it_grants_nothing')}</p>
            <p className="org__fine">{t('organizations.affiliation_fine')}</p>
          </section>

          <section className="org__card">
            <p className="org__eyebrow">{t('organizations.why_curated_title')}</p>
            <p className="org__body">{t('organizations.why_curated_desc')}</p>
            <p className="org__fine">{t('organizations.why_curated_fine')}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}

function Row({
  organization,
  affiliated,
  busy,
  onJoin,
  onLeave,
}: {
  organization: Organization;
  affiliated: boolean;
  busy: boolean;
  onJoin: () => void;
  onLeave: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  return (
    <li className="org__row">
      <div className="org__row-main">
        <div className="org__row-name">{organization.name}</div>
        <div className="org__row-meta">
          {ORGANIZATION_KIND_LABELS[organization.kind]}
          {organization.parent_name ? ` · ${organization.parent_name}` : ''}
        </div>
        {/* Shown when there is more than the canonical name, so somebody who searched a variant
            can see why this row came back. */}
        {organization.aliases.length > 1 ? (
          <div className="org__aliases">
            {t('organizations.also_known_as')} {organization.aliases.slice(1).join(', ')}
          </div>
        ) : null}
      </div>

      <div className="org__row-acts">
        {/* Offered to everyone: the page itself is readable by anyone who can see the registry
            entry, and it says plainly when nothing on it can be changed. */}
        <Link className="org__ghost" to="/organizations/$id/admin" params={{ id: organization.id }}>
          {t('organizations.details')}
        </Link>

        <button
          type="button"
          className={affiliated ? 'org__btn org__btn--quiet' : 'org__btn'}
          disabled={busy}
          onClick={affiliated ? onLeave : onJoin}
        >
          {affiliated ? t('organizations.unaffiliate') : t('organizations.affiliate')}
        </button>
      </div>
    </li>
  );
}

function RequestForm({
  onDone,
  onCancel,
}: {
  onDone: () => Promise<void>;
  onCancel: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('common');
  const [name, setName] = useState('');
  const [kind, setKind] = useState<OrganizationKind>('parish');
  const [detail, setDetail] = useState('');

  // Checked before submitting, so nobody waits days for a review of a name that already exists
  // under a different spelling.
  const duplicate = useQuery({
    queryKey: ['organization-name-check', name],
    queryFn: () => checkName(name),
    enabled: name.trim().length > 3,
  });

  const submit = useMutation({
    mutationFn: () => requestOrganization({ name, kind, detail }),
    onSuccess: onDone,
  });

  const error = submit.error instanceof Error ? (submit.error as ApiError) : undefined;
  const existing = duplicate.data?.existing ?? null;

  return (
    <section className="org__card">
      <p className="org__eyebrow">{t('organizations.request_form.title')}</p>

      <label className="org__field">
        <span className="org__field-label">{t('organizations.request_form.legal_name')}</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Diocese of…" />
      </label>

      {existing ? (
        <p className="org__found">
          <strong>{existing.name}</strong> is already in the registry — affiliate with that instead.
        </p>
      ) : null}

      <label className="org__field">
        <span className="org__field-label">{t('organizations.request_form.kind')}</span>
        <select value={kind} onChange={(e) => setKind(e.target.value as OrganizationKind)}>
          {Object.entries(ORGANIZATION_KIND_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>

      <label className="org__field">
        <span className="org__field-label">{t('organizations.request_form.anything_helpful')}</span>
        <textarea
          rows={2}
          value={detail}
          onChange={(e) => setDetail(e.target.value)}
          placeholder="City, diocese, or anything that tells one Saint Mary's from another"
        />
      </label>

      {error ? <Notice referenceId={error.correlationId}>{error.message}</Notice> : null}

      <div className="org__actions">
        <button
          type="button"
          className="org__btn"
          disabled={submit.isPending || name.trim() === '' || existing !== null}
          onClick={() => submit.mutate()}
        >
          {submit.isPending
            ? t('organizations.request_form.submitting')
            : t('organizations.request_form.submit')}
        </button>
        <button type="button" className="org__ghost" onClick={onCancel}>
          {t('organizations.request_form.cancel')}
        </button>
      </div>

      <p className="org__fine">{t('organizations.request_form.note')}</p>
    </section>
  );
}
